import React from 'react'
import { useBrickStore } from '@store/brickStore'
import { designTokens } from '@styles/design-tokens'
import type { BrickColor } from '@models/brick'
import './ColorPicker.css'

export interface ColorSwatchProps {
  label: string
  color: BrickColor
}

/**
 * ColorPicker Component
 *
 * Grid of circular color swatches for brick color selection
 *
 * Features (Task 3.1.2):
 * - 6 predefined brick colors from design tokens
 * - Circular swatches (40px diameter)
 * - Visual selection feedback (4px white border + 2px color ring)
 * - Responsive layout (single row on mobile, grid on desktop)
 * - Full keyboard accessibility with ARIA labels
 * - Connected to brickStore's selectedColor and setSelectedColor
 *
 * Design specs from DESIGN_GUIDELINES.md:
 * - Swatch size: 40px diameter circles
 * - Selected: 4px white border + 2px color ring
 * - Spacing: 8px gap between swatches
 * - Shadow on selected: 0 0 0 4px rgba(color, 0.2)
 *
 * @component
 */
export const ColorPicker: React.FC = () => {
  const selectedColor = useBrickStore((state) => state.selectedColor)
  const setSelectedColor = useBrickStore((state) => state.setSelectedColor)

  // Color palette from DESIGN_GUIDELINES.md brick colors
  const colorPalette: Array<ColorSwatchProps> = [
    {
      label: 'Red',
      color: { r: 220, g: 50, b: 50 },
    },
    {
      label: 'Blue',
      color: { r: 50, g: 100, b: 220 },
    },
    {
      label: 'Green',
      color: { r: 50, g: 200, b: 80 },
    },
    {
      label: 'Yellow',
      color: { r: 255, g: 220, b: 50 },
    },
    {
      label: 'Orange',
      color: { r: 255, g: 140, b: 50 },
    },
    {
      label: 'Purple',
      color: { r: 150, g: 50, b: 200 },
    },
  ]

  const isColorSelected = (color: BrickColor): boolean => {
    return (
      selectedColor.r === color.r &&
      selectedColor.g === color.g &&
      selectedColor.b === color.b
    )
  }

  const handleColorSelect = (color: BrickColor) => {
    setSelectedColor(color)
  }

  const handleKeyDown = (e: React.KeyboardEvent, color: BrickColor) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleColorSelect(color)
    }
  }

  const colorToRgbString = (color: BrickColor): string => {
    return `rgb(${color.r}, ${color.g}, ${color.b})`
  }

  return (
    <div className="color-picker">
      <div className="color-picker__label">BRICK COLOR</div>
      <div className="color-picker__swatches" role="radiogroup" aria-label="Brick color selection">
        {colorPalette.map((swatch) => {
          const isSelected = isColorSelected(swatch.color)
          const rgbString = colorToRgbString(swatch.color)

          return (
            <button
              key={swatch.label}
              className={`color-picker__swatch ${isSelected ? 'color-picker__swatch--selected' : ''}`}
              style={{
                backgroundColor: rgbString,
                boxShadow: isSelected
                  ? `0 0 0 4px rgba(${swatch.color.r}, ${swatch.color.g}, ${swatch.color.b}, 0.2)`
                  : undefined,
              }}
              onClick={() => handleColorSelect(swatch.color)}
              onKeyDown={(e) => handleKeyDown(e, swatch.color)}
              role="radio"
              aria-checked={isSelected}
              aria-label={`${swatch.label} brick color`}
              tabIndex={0}
              title={swatch.label}
            />
          )
        })}
      </div>
    </div>
  )
}
